import { Box, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { format as formatSql } from 'sql-formatter';

import { Button, Icon, Tooltip } from '@verantos-dev/shared-components';

const COPY_ICON_SIZE = 16;
const COPY_BTN_TOP_RIGHT_POS = 14;
const COPY_BTN_BORDER_RADIUS = 2;

const CopyIcon = () => {
  const theme = useTheme();
  return (
    <Icon
      id="copy-sql-icon"
      icon="copy"
      color="info"
      height={COPY_ICON_SIZE}
      width={COPY_ICON_SIZE}
      sx={{ fill: theme.palette.primary.main }}
    />
  );
};

type UseCohortDefinitionSqlContentParams = {
  sqlQuery: string;
};

export const useCohortDefinitionSqlContent = ({
  sqlQuery,
}: UseCohortDefinitionSqlContentParams) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  // Try to format the SQL query
  let formattedSqlQuery;
  try {
    formattedSqlQuery = formatSql(sqlQuery, { language: 'snowflake' });
  } catch {
    formattedSqlQuery = sqlQuery; // Fallback to original one if formatting fails
  }

  const onCopy = () => {
    if (!copied) {
      navigator.clipboard.writeText(formattedSqlQuery);
      setCopied(true);
    }
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000); // 2 seconds

      return () => clearTimeout(timer);
    }
  }, [copied]);

  const sqlContent = formattedSqlQuery ? (
    <Box sx={{ position: 'relative' }}>
      <Tooltip
        id="copy-sql-tooltip"
        title={intl.formatMessage({
          id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copiedToClipboard',
        })}
        open={copied}
        width="auto"
        placement="top"
      >
        <Button
          id="copy-sql-button"
          displayLabel={
            copied
              ? intl.formatMessage({
                  id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copied',
                })
              : intl.formatMessage({
                  id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copy',
                })
          }
          onClickHandler={onCopy}
          btnType="secondary"
          variant="outlined"
          sx={{
            position: 'absolute',
            top: COPY_BTN_TOP_RIGHT_POS,
            right: COPY_BTN_TOP_RIGHT_POS,
            borderRadius: COPY_BTN_BORDER_RADIUS,
            width: 115,
          }}
          startIcon={<CopyIcon />}
        />
      </Tooltip>

      <SyntaxHighlighter language="sql" style={oneLight}>
        {formattedSqlQuery}
      </SyntaxHighlighter>
    </Box>
  ) : undefined;

  return {
    sqlContent,
  };
};
